import { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  Typography,
  Grid,
  Chip,
  Tabs,
  Tab,
  Paper,
  Divider,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { CreateZoneRequest, AudioDevice, MusicFolderNode } from '../../models/Zone';
import { ZoneCategory } from '../../models/ZoneCategory';
// Import new zone service for improved playlist handling
import * as zoneService from '../../services/newZoneService';
import * as hotelService from '../../services/hotelService';
import { Hotel } from '../../models/Hotel';
import { useAuth } from '../../hooks/useAuth';
import FolderTree from './FolderTree';
import SchedulesTab from './SchedulesTab';

interface ZoneFormProps {
  onSuccess: () => void;
  hotelId?: number;
  initialData?: CreateZoneRequest;
  isEditing?: boolean;
  zoneId?: number;
  categories?: ZoneCategory[]; // Available categories for selection
  initialTab?: number; // 0 = General, 1 = Schedules
}

export const ZoneForm = ({
  onSuccess,
  hotelId,
  initialData,
  isEditing = false,
  zoneId,
  categories = [],
  initialTab = 0
}: ZoneFormProps) => {
  const { isHotelManager, user } = useAuth();

  // Get the correct hotelId - from props, or from user for HotelManager
  // Make sure to provide a fallback number (0) for TypeScript
  const effectiveHotelId = hotelId ?? (isHotelManager && user?.hotelId ? user.hotelId : 0);
  
  // Use initialData if provided (for editing), otherwise use defaults
  const [formData, setFormData] = useState<CreateZoneRequest>(initialData ?? {
    name: '',
    deviceName: '',
    outputChannel: '',
    musicFolderPath: '',
    musicFolderPaths: [], // New field for multiple folders
    hotelId: effectiveHotelId,
    categoryId: null, // Category selection
  });

  const [hotels, setHotels] = useState<Hotel[]>([]);
  const [audioDevices, setAudioDevices] = useState<AudioDevice[]>([]);
  const [musicFolders, setMusicFolders] = useState<string[]>([]);
  const [musicSubfolders, setMusicSubfolders] = useState<{[key: string]: string[]}>({});
  const [musicFoldersTree, setMusicFoldersTree] = useState<MusicFolderNode[]>([]);
  const [selectedMusicFolders, setSelectedMusicFolders] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<number[]>([]);
  const [channelConfiguration, setChannelConfiguration] = useState<'Mono' | 'Stereo' | 'MultiChannel'>('Stereo');
  const [selectedDevice, setSelectedDevice] = useState<AudioDevice | null>(null);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateZoneRequest, string>>>({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [loadingData, setLoadingData] = useState(true);
  const [folderViewTab, setFolderViewTab] = useState(0); // 0 = Tree View, 1 = List View
  const [formTab, setFormTab] = useState(initialTab); // 0 = General, 1 = Schedules
  const [channelConfigOpen, setChannelConfigOpen] = useState(false);
  const [refreshingDevices, setRefreshingDevices] = useState(false);
  const [deviceAnalysisInfo, setDeviceAnalysisInfo] = useState<string>('');
  const [asioDevices, setAsioDevices] = useState<AudioDevice[]>([]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoadingData(true);
        
        // Add individual try/catch for each API call to handle failures gracefully
        let devicesData: AudioDevice[] = [];
        let foldersData: string[] = [];
        
        try {
          // CRITICAL FIX: Skip ASIO analysis when ZoneForm is used in modal context
          // This prevents "Unable to instantiate ASIO" errors during active playback
          devicesData = await zoneService.getAudioDevices(true);
          console.log('Loaded audio devices in modal-safe mode (no ASIO conflicts)');
        } catch (err) {
          console.error('Failed to load audio devices:', err);
          // Will use fallback devices from the service
        }
        
        // Try to load ASIO devices in modal-safe mode
        try {
          // CRITICAL FIX: Skip ASIO driver instantiation when loading in modal context
          const asioData = await zoneService.getAsioDevices(true);
          setAsioDevices(asioData);
          console.log('Loaded ASIO devices in modal-safe mode (cached, no driver conflicts):', asioData);
        } catch (err) {
          console.error('Failed to load ASIO devices:', err);
          // ASIO devices are optional
        }
        
        try {
          // Fetch folder tree for hierarchical display
          const foldersTree = await zoneService.getMusicFoldersTree();
          setMusicFoldersTree(foldersTree);
          
          // Also get flat folder list for backward compatibility
          foldersData = await zoneService.getMusicFolders();
          
          // Fetch subfolders for each main folder
          // CRITICAL FIX: Skip ASIO analysis when loading subfolders in modal context
          const subfoldersPromises = foldersData.map(async folder => {
            try {
              const subfolders = await zoneService.getMusicSubfolders(folder, true); // skipAsioAnalysis=true
              return { folder, subfolders };
            } catch (err) {
              console.error(`Failed to load subfolders for ${folder}:`, err);
              return { folder, subfolders: [] };
            }
          });
          
          const subfoldersResults = await Promise.all(subfoldersPromises);
          const subfoldersMap: {[key: string]: string[]} = {};
          
          subfoldersResults.forEach(result => {
            subfoldersMap[result.folder] = result.subfolders;
          });
          
          setMusicSubfolders(subfoldersMap);
          
          // If initialData has musicFolderPaths, set it as selected
          if (initialData?.musicFolderPaths && initialData.musicFolderPaths.length > 0) {
            setSelectedMusicFolders(initialData.musicFolderPaths);
          } else if (initialData?.musicFolderPath) {
            // Backward compatibility for single path
            setSelectedMusicFolders([initialData.musicFolderPath]);
          }
        } catch (err) {
          console.error('Failed to load music folders:', err);
          // Will use fallback folders from the service
        }
        
        setAudioDevices(devicesData);
        setMusicFolders(foldersData);
        
        // Only Hotel Manager without a pre-selected hotelId needs to select a hotel
        if (isHotelManager && !hotelId) {
          try {
            const hotelsData = await hotelService.getHotels();
            setHotels(hotelsData);
          } catch (err) {
            console.error('Failed to load hotels:', err);
            setSubmitError('Failed to load hotels data. Some features may be limited.');
          }
        }
        

        // For editing mode with a deviceName, set available channels and select device
        if (initialData?.deviceName) {
          const device = devicesData.find(d => d.name === initialData.deviceName);
          if (device) {
            setSelectedDevice(device);
            setChannels(device.channels || []);
          }
        }
      } catch (error) {
        console.error('Error in fetchInitialData:', error);
        setSubmitError('Some data could not be loaded. You may proceed, but some features might be limited.');
      } finally {
        setLoadingData(false);
      }
    };

    fetchInitialData();
  }, [isHotelManager, hotelId, initialData]);


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when field is edited
    if (errors[name as keyof CreateZoneRequest]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ ...prev, [name]: value }));

    // If device changes, update available channels
    if (name === 'deviceName') {
      const device = audioDevices.find(d => d.name === value);
      setChannels(device?.channels || []);
      // Reset output channel when device changes
      setFormData(prev => ({ ...prev, deviceName: value, outputChannel: '' }));
    }

    // Clear error when field is edited
    if (errors[name as keyof CreateZoneRequest]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFolderSelectionChange = (selectedPaths: string[]) => {
    setSelectedMusicFolders(selectedPaths);
    setFormData(prev => ({
      ...prev,
      musicFolderPaths: selectedPaths,
      musicFolderPath: selectedPaths.length > 0 ? selectedPaths[0] : ''
    }));
    
    // Clear error when field is edited
    if (errors.musicFolderPath) {
      setErrors(prev => ({ ...prev, musicFolderPath: '' }));
    }
  };

  const toggleMusicFolder = (folder: string) => {
    const isSelected = selectedMusicFolders.includes(folder);
    const newSelection = isSelected
      ? selectedMusicFolders.filter(f => f !== folder)
      : [...selectedMusicFolders, folder];
    handleFolderSelectionChange(newSelection);
  };

  const handleDeviceSelection = (deviceName: string) => {
    const device = audioDevices.find(d => d.name === deviceName);
    setSelectedDevice(device || null);
    
    if (device) {
      setChannels(device.channels || []);
      
      // Reset channel selections when device changes
      setSelectedChannels([]);
      setChannelConfiguration('Stereo');
      
      // Update form data
      setFormData(prev => ({
        ...prev,
        deviceName: deviceName,
        outputChannel: '' // Reset output channel
      }));
      
      // Clear device error
      if (errors.deviceName) {
        setErrors(prev => ({ ...prev, deviceName: '' }));
      }
    }
  };

  const handleChannelSelectionChange = (channelIndices: number[]) => {
    setSelectedChannels(channelIndices);
    
    // Update channel configuration based on selection
    const channelCount = channelIndices.length;
    const newConfig = channelCount === 1 ? 'Mono' : 
                      channelCount === 2 ? 'Stereo' : 'MultiChannel';
    setChannelConfiguration(newConfig);
    
    // For backward compatibility, set outputChannel to first selected channel
    if (channelIndices.length > 0 && selectedDevice) {
      const firstChannelName = selectedDevice.channels?.[channelIndices[0]] || 'Both';
      setFormData(prev => ({
        ...prev,
        outputChannel: firstChannelName,
        outputChannels: channelIndices,
        channelConfiguration: newConfig
      }));
    }
    
    // Clear output channel error
    if (errors.outputChannel) {
      setErrors(prev => ({ ...prev, outputChannel: '' }));
    }
  };

  const handleRefreshDevices = async () => {
    setRefreshingDevices(true);
    setDeviceAnalysisInfo('Refreshing audio devices...');
    
    try {
      const result = await zoneService.refreshAudioDevices();
      
      if (result.success) {
        setDeviceAnalysisInfo(`Found ${result.deviceCount} audio devices`);
        
        // Reload audio devices from updated cache (safe, no ASIO probe)
        // The refresh endpoint already updated the cache with safe detection
        const newDevices = await zoneService.getAudioDevices(true);
        setAudioDevices(newDevices);
        
        // Clear device selection to force re-selection
        setSelectedDevice(null);
        setFormData(prev => ({ ...prev, deviceName: '', outputChannel: '' }));
      } else {
        setDeviceAnalysisInfo(result.message || 'Failed to refresh devices');
      }
    } catch (error) {
      console.error('Error refreshing devices:', error);
      setDeviceAnalysisInfo('Error refreshing devices');
    } finally {
      setRefreshingDevices(false);
      // Clear message after 5 seconds
      setTimeout(() => setDeviceAnalysisInfo(''), 5000);
    }
  };

  const handleAnalyzeDevice = async (deviceName: string) => {
    if (!deviceName) return;
    
    try {
      setDeviceAnalysisInfo('Analyzing device...');
      const analysis = await zoneService.analyzeSpecificDevice(deviceName);
      
      const message = `${analysis.Name}: ${analysis.OutputChannelCount} channels (${analysis.DeviceType}), Independent: ${analysis.SupportsIndependentChannels ? 'Yes' : 'No'}`;
      setDeviceAnalysisInfo(message);
      
      console.log('Device analysis:', analysis);
      
      // Clear message after 10 seconds
      setTimeout(() => setDeviceAnalysisInfo(''), 10000);
    } catch (error) {
      console.error('Error analyzing device:', error);
      setDeviceAnalysisInfo('Error analyzing device');
      setTimeout(() => setDeviceAnalysisInfo(''), 5000);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreateZoneRequest, string>> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Zone name is required';
    }
    
    if (!formData.deviceName) {
      newErrors.deviceName = 'Device is required';
    }
    
    if (!formData.outputChannel && selectedChannels.length === 0) {
      newErrors.outputChannel = 'At least one output channel is required';
    }
    
    // Klasör seçme zorunluluğunu kaldırıyoruz
    // Artık müzik klasörleri sadece Event ekleme kısmında seçilecek
    
    // Hotel Manager without pre-selected hotel needs to select a hotel
    if (isHotelManager && !hotelId && !formData.hotelId) {
      newErrors.hotelId = 'Hotel is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      setSubmitError('');
      
      // Ensure hotelId is set correctly
      // For HotelManager, always use their own hotel ID
      const submissionHotelId = isHotelManager && user?.hotelId
        ? user.hotelId
        : (hotelId ?? formData.hotelId);
      
      // Ensure hotelId is a number for TypeScript
      if (typeof submissionHotelId !== 'number' || isNaN(submissionHotelId)) {
        throw new Error('A valid hotel ID is required');
      }
      
      // Sanitize data before submission
      const dataToSubmit = {
        ...formData,
        hotelId: submissionHotelId,
        musicFolderPath: formData.musicFolderPath || '',
        musicFolderPaths: formData.musicFolderPaths || []
      };
      
      console.log(`Submitting zone data:`, dataToSubmit);
      
      try {
        let createdZoneId = zoneId;
        
        if (isEditing && zoneId) {
          // Update existing zone
          await zoneService.updateZone(zoneId, dataToSubmit);
        } else {
          // Create new zone
          const result = await zoneService.createZone(dataToSubmit);
          createdZoneId = result.id;
        }
        
        // Create channel mappings if multi-channel configuration is used
        if (selectedChannels.length > 0 && selectedDevice?.supportsIndependentChannels && createdZoneId) {
          const channelMappings = selectedChannels.map((channelIndex, idx) => ({
            deviceChannelIndex: channelIndex,
            volume: 1.0,
            isActive: true,
            channelName: selectedDevice.channelDetails?.[channelIndex]?.name || `Channel ${channelIndex + 1}`
          }));
          
          try {
            await zoneService.setZoneChannelMappings(createdZoneId, channelMappings);
            console.log(`Created ${channelMappings.length} channel mappings for zone ${createdZoneId}`);
          } catch (channelError) {
            console.warn('Failed to create channel mappings, zone created with default settings:', channelError);
          }
        }
        
        // Only proceed to success if no errors were thrown
        onSuccess();
      } catch (apiError: any) {
        console.error(`API error ${isEditing ? 'updating' : 'creating'} zone:`, apiError);
        
        // Handle specific error cases
        if (apiError.message && apiError.message.includes('music folder')) {
          setErrors(prev => ({ ...prev, musicFolderPath: 'There was an issue with the selected music folders. Please check your selection.' }));
        }
        
        throw apiError; // Re-throw to be caught by the outer catch
      }
    } catch (error: any) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} zone:`, error);
      
      // Ensure we're handling the error properly and not setting an object directly
      let errorMessage = error.message || `Failed to ${isEditing ? 'update' : 'create'} zone. Please try again.`;
      
      // Format the error message to be more user-friendly
      if (errorMessage.includes('500')) {
        errorMessage = `Server error occurred. Please try again or contact support if the problem persists.`;
      }
      
      setSubmitError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Modern input style
  const modernInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#fafafa',
      '& fieldset': { borderColor: '#e8e8e8' },
      '&:hover fieldset': { borderColor: 'rgba(66, 148, 227, 0.4)' },
      '&.Mui-focused fieldset': { borderColor: '#4294e3', borderWidth: '2px' },
    },
    '& .MuiInputLabel-root': {
      color: '#888',
      '&.Mui-focused': { color: '#4294e3' }
    },
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
      {submitError && (
        <Alert
          severity="error"
          sx={{
            mb: 2, borderRadius: '12px',
            backgroundColor: 'rgba(229, 57, 53, 0.05)',
            border: '1px solid rgba(229, 57, 53, 0.15)',
            color: '#c62828',
            '& .MuiAlert-icon': { color: '#c62828' }
          }}
        >
          {submitError}
        </Alert>
      )}

      {/* Main Tab Navigation */}
      {isEditing && (
        <Box sx={{ mb: 3 }}>
          <Tabs
            value={formTab}
            onChange={(e, newValue) => setFormTab(newValue)}
            aria-label="zone form tabs"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#4294e3',
                height: 3,
                borderRadius: '3px 3px 0 0'
              },
              borderBottom: '1px solid #f0f0f0'
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsRoundedIcon sx={{ fontSize: 16 }} />
                  Genel Ayarlar
                </Box>
              }
              sx={{
                textTransform: 'none', fontWeight: 500, fontSize: '0.9rem',
                color: '#888', '&.Mui-selected': { color: '#4294e3', fontWeight: 600 }
              }}
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayRoundedIcon sx={{ fontSize: 16 }} />
                  Zamanlama
                </Box>
              }
              sx={{
                textTransform: 'none', fontWeight: 500, fontSize: '0.9rem',
                color: '#888', '&.Mui-selected': { color: '#4294e3', fontWeight: 600 }
              }}
            />
          </Tabs>
        </Box>
      )}

      {/* General Settings Tab */}
      <Box hidden={formTab !== 0}>
        {/* Zone Name */}
        <Box sx={{ mb: 2.5 }}>
          <TextField
            name="name"
            label="Zone Adı"
            fullWidth
            required
            value={formData.name}
            onChange={handleInputChange}
            error={!!errors.name}
            helperText={errors.name}
            sx={modernInputSx}
          />
        </Box>

        {/* Zone Category Selection */}
        {categories.length > 0 && (
          <Box sx={{ mb: 2.5 }}>
            <FormControl fullWidth sx={modernInputSx}>
              <InputLabel>Kategori (Opsiyonel)</InputLabel>
              <Select
                name="categoryId"
                value={formData.categoryId || ''}
                label="Kategori (Opsiyonel)"
                onChange={(e) => setFormData({ ...formData, categoryId: e.target.value as number || null })}
              >
                <MenuItem value="">
                  <em>Kategori seçmeyin</em>
                </MenuItem>
                {categories.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <i className={category.icon} style={{ fontSize: '16px', color: category.color }} />
                      <span>{category.name}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={{ color: '#aaa' }}>
                Zone'u bir kategoriye atayarak filtreleme yapabilirsiniz
              </FormHelperText>
            </FormControl>
          </Box>
        )}

        {isHotelManager && !hotelId && (
          <Box sx={{ mb: 2.5 }}>
            <FormControl fullWidth required error={!!errors.hotelId} sx={modernInputSx}>
              <InputLabel>Hotel</InputLabel>
              <Select
                name="hotelId"
                value={formData.hotelId}
                label="Hotel"
                onChange={handleSelectChange}
              >
                {hotels.map(hotel => (
                  <MenuItem key={hotel.id} value={hotel.id}>
                    {hotel.name}
                  </MenuItem>
                ))}
              </Select>
              {errors.hotelId && <FormHelperText>{errors.hotelId}</FormHelperText>}
            </FormControl>
          </Box>
        )}

        {/* Default Music Folders - moved up, right after Zone Name */}
        <Box sx={{
          mb: 2.5, p: 2, borderRadius: '14px',
          border: '1px solid #f0f0f0',
          backgroundColor: '#fafcff'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FolderOpenRoundedIcon sx={{ fontSize: 18, color: '#4294e3' }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a1a2e' }}>
                Varsayılan Müzik Klasörleri
              </Typography>
              <Tooltip title="Schedule harici varsayılan müzik klasörleri. Schedule aktif değilken bu klasörlerden müzik çalar.">
                <InfoOutlinedIcon sx={{ fontSize: 15, color: '#bbb', cursor: 'pointer' }} />
              </Tooltip>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                onClick={() => {
                  const getAllPaths = (nodes: MusicFolderNode[]): string[] => {
                    let paths: string[] = [];
                    nodes.forEach((node) => {
                      paths.push(node.path);
                      if (node.children && node.children.length > 0) {
                        paths = [...paths, ...getAllPaths(node.children)];
                      }
                    });
                    return paths;
                  };
                  let allPaths: string[] = [];
                  if (musicFoldersTree.length > 0) {
                    allPaths = getAllPaths(musicFoldersTree);
                  } else {
                    musicFolders.forEach((folder) => {
                      allPaths.push(folder);
                      const subs = musicSubfolders[folder] || [];
                      subs.forEach((sub) => allPaths.push(`${folder}/${sub}`));
                    });
                  }
                  handleFolderSelectionChange(allPaths);
                }}
                sx={{
                  textTransform: 'none', fontSize: '0.7rem', fontWeight: 500,
                  color: '#4294e3', borderRadius: '8px', minWidth: 0, px: 1.5, py: 0.5,
                  '&:hover': { backgroundColor: 'rgba(66, 148, 227, 0.08)' }
                }}
              >
                Tümünü Seç
              </Button>
              <Button
                size="small"
                onClick={() => handleFolderSelectionChange([])}
                sx={{
                  textTransform: 'none', fontSize: '0.7rem', fontWeight: 500,
                  color: '#999', borderRadius: '8px', minWidth: 0, px: 1.5, py: 0.5,
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                }}
              >
                Temizle
              </Button>
            </Box>
          </Box>

          {/* Tree View Only */}
          <Box sx={{
            borderRadius: '10px',
            border: '1px solid #e8e8e8',
            overflow: 'hidden',
            backgroundColor: '#fff'
          }}>
            <FolderTree
              nodes={musicFoldersTree}
              selectedPaths={selectedMusicFolders}
              onSelectionChange={handleFolderSelectionChange}
            />
          </Box>

          {/* Selected folders summary */}
          {selectedMusicFolders.length > 0 && (
            <Box sx={{
              mt: 1.5, p: 1.5,
              display: 'flex', flexWrap: 'wrap', gap: 0.5,
              backgroundColor: 'rgba(66, 148, 227, 0.04)',
              borderRadius: '10px', border: '1px solid rgba(66, 148, 227, 0.12)',
            }}>
              <Typography sx={{
                width: '100%', mb: 0.5,
                fontSize: '0.75rem', fontWeight: 600, color: '#4294e3',
                display: 'flex', alignItems: 'center', gap: 0.5,
              }}>
                <CheckCircleRoundedIcon sx={{ fontSize: 14 }} />
                Seçili klasörler ({selectedMusicFolders.length}):
              </Typography>
              {selectedMusicFolders.map((folder) => (
                <Chip
                  key={folder}
                  label={folder}
                  size="small"
                  sx={{
                    fontWeight: 500, fontSize: '0.75rem', height: 28,
                    borderRadius: '8px',
                    backgroundColor: 'rgba(66, 148, 227, 0.08)',
                    color: '#4294e3',
                    border: '1px solid rgba(66, 148, 227, 0.15)',
                    '& .MuiChip-deleteIcon': { color: 'rgba(66, 148, 227, 0.5)', fontSize: 16 },
                    '&:hover': { backgroundColor: 'rgba(66, 148, 227, 0.12)' },
                  }}
                  onDelete={() => {
                    const newSelected = selectedMusicFolders.filter((f) => f !== folder);
                    handleFolderSelectionChange(newSelected);
                  }}
                  deleteIcon={<CloseRoundedIcon />}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Channel Configuration - Collapsible */}
        <Box sx={{
          mb: 2.5, borderRadius: '14px',
          border: '1px solid #f0f0f0',
          overflow: 'hidden'
        }}>
          {/* Collapsible Header */}
          <Box
            onClick={() => setChannelConfigOpen(!channelConfigOpen)}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2, py: 1.5, cursor: 'pointer',
              backgroundColor: channelConfigOpen ? '#fafcff' : '#fafafa',
              transition: 'background-color 0.2s',
              '&:hover': { backgroundColor: '#f5f8ff' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TuneRoundedIcon sx={{ fontSize: 18, color: '#4294e3' }} />
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a1a2e', lineHeight: 1.3 }}>
                  Kanal Yapılandırması
                </Typography>
                <Typography sx={{ fontSize: '0.7rem', color: '#aaa', lineHeight: 1.3 }}>
                  {formData.deviceName
                    ? `${formData.deviceName}${selectedChannels.length > 0 ? ` — ${selectedChannels.length} kanal seçili` : ''}`
                    : 'Ses cihazı ve kanal ayarları'
                  }
                </Typography>
              </Box>
            </Box>
            <ExpandMoreRoundedIcon sx={{
              fontSize: 20, color: '#999',
              transform: channelConfigOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s'
            }} />
          </Box>

          {/* Collapsible Content */}
          <Collapse in={channelConfigOpen}>
            <Box sx={{ p: 2, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
              {/* Audio Device */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FormControl fullWidth required error={!!errors.deviceName} sx={modernInputSx}>
                  <InputLabel>Ses Cihazı</InputLabel>
                  <Select
                    name="deviceName"
                    value={formData.deviceName}
                    onChange={(e) => handleDeviceSelection(e.target.value as string)}
                    label="Ses Cihazı"
                  >
                    {audioDevices.map(device => (
                      <MenuItem
                        key={device.name}
                        value={device.name}
                        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: 1 }}
                      >
                        <Typography variant="body2">{device.name}</Typography>
                        {device.deviceType && (
                          <Typography variant="caption" color="text.secondary">
                            {device.deviceType} {device.outputChannelCount && `(${device.outputChannelCount} kanal)`}
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.deviceName && <FormHelperText>{errors.deviceName}</FormHelperText>}
                </FormControl>
                <Button
                  onClick={handleRefreshDevices}
                  disabled={refreshingDevices}
                  sx={{
                    minWidth: 44, height: 44, borderRadius: '12px',
                    backgroundColor: '#f5f5f5', color: '#666',
                    '&:hover': { backgroundColor: '#eee' }
                  }}
                >
                  {refreshingDevices ? <CircularProgress size={18} /> : <SyncRoundedIcon sx={{ fontSize: 18 }} />}
                </Button>
              </Box>

              {/* Device analysis information */}
              {deviceAnalysisInfo && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.8rem' }}>
                  {deviceAnalysisInfo}
                </Alert>
              )}

              {/* Channel Selection */}
              {selectedDevice && (
                <Box sx={{
                  p: 2, borderRadius: '12px',
                  backgroundColor: '#fafafa',
                  border: '1px solid #e8e8e8'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#1a1a2e' }}>
                      Çıkış Kanalları
                    </Typography>
                    <Chip
                      label={channelConfiguration}
                      size="small"
                      sx={{
                        height: 22, fontSize: '0.65rem', fontWeight: 600,
                        backgroundColor: 'rgba(66, 148, 227, 0.1)',
                        color: '#4294e3', borderRadius: '6px'
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedDevice.channelDetails?.map((channel) => {
                      const isChecked = selectedChannels.includes(channel.index);
                      return (
                        <Box
                          key={channel.index}
                          onClick={() => {
                            if (isChecked) {
                              handleChannelSelectionChange(selectedChannels.filter(c => c !== channel.index));
                            } else {
                              handleChannelSelectionChange([...selectedChannels, channel.index]);
                            }
                          }}
                          sx={{
                            display: 'flex', alignItems: 'center', gap: 1,
                            px: 1.5, py: 0.8, borderRadius: '10px', cursor: 'pointer',
                            border: isChecked ? '2px solid #4294e3' : '1px solid #e0e0e0',
                            backgroundColor: isChecked ? 'rgba(66, 148, 227, 0.06)' : '#fff',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: isChecked ? '#4294e3' : '#bbb' }
                          }}
                        >
                          <Checkbox
                            checked={isChecked}
                            size="small"
                            sx={{
                              p: 0, color: '#ccc',
                              '&.Mui-checked': { color: '#4294e3' }
                            }}
                          />
                          <Box>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: isChecked ? 600 : 400, color: isChecked ? '#4294e3' : '#1a1a2e' }}>
                              {channel.name}
                            </Typography>
                            {channel.description && (
                              <Typography sx={{ fontSize: '0.65rem', color: '#aaa' }}>
                                {channel.description}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                  {selectedChannels.length === 0 && (
                    <Typography sx={{ fontSize: '0.75rem', color: '#c62828', mt: 1 }}>
                      En az bir çıkış kanalı seçin
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>

      {/* Schedules Tab */}
      <Box hidden={formTab !== 1} sx={{ pb: 2 }}>
        <SchedulesTab
          zoneId={zoneId}
          zoneName={formData.name}
          hotelId={formData.hotelId}
          isEditing={isEditing}
          afterSave={() => {
            // Could refresh zone data here if needed
          }}
        />
      </Box>

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          disabled={loading}
          sx={{
            minWidth: 140, py: 1.2, borderRadius: '12px',
            textTransform: 'none', fontWeight: 600, fontSize: '0.9rem',
            backgroundColor: '#4294e3', color: '#fff',
            '&:hover': { backgroundColor: '#3680c9' },
            '&:disabled': { backgroundColor: 'rgba(66, 148, 227, 0.3)', color: '#fff' }
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : (isEditing ? 'Güncelle' : 'Oluştur')}
        </Button>
      </Box>
    </Box>
  );
};